import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const WelcomeSection = ({ onContinue }) => {
  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-card border border-border rounded-lg p-8 shadow-sm">
        <div className="flex justify-center mb-6">
          <div className="bg-primary/10 p-6 rounded-full">
            <Icon name="TrendingUp" className="w-12 h-12 text-primary" />
          </div>
        </div>

        <h1 className="text-3xl font-bold text-text-primary text-center mb-4">
          Welcome to Maturation Monitor
        </h1>

        <p className="text-text-secondary text-center mb-8 text-lg">
          Track your child's growth and development with evidence-based insights
        </p>

        <div className="space-y-6 mb-8">
          <div className="bg-surface rounded-lg p-6">
            <h2 className="text-xl font-semibold text-text-primary mb-4">Purpose</h2>
            <p className="text-text-secondary leading-relaxed">
              This application helps parents and guardians track longitudinal growth patterns, 
              estimate peak height velocity timing using repeated height measurements, and provides 
              evidence-based, non-diagnostic guidance for age-appropriate sport and exercise activities.
            </p>
          </div>

          <div className="bg-warning/10 border border-warning rounded-lg p-6">
            <div className="flex items-start">
              <Icon name="AlertTriangle" className="w-6 h-6 text-warning mr-3 mt-1 flex-shrink-0" />
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-text-primary mb-3">Important Safety Caveats</h3>
                
                <div className="bg-card rounded-lg p-4 border border-warning/30">
                  <p className="text-sm font-medium text-text-primary mb-1">Purpose and Limits</p>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    This app is for education and self monitoring. It does not provide medical advice, it does not diagnose conditions, and it cannot predict injuries. If you are worried about your child's health, growth, pain, or wellbeing, contact a qualified healthcare professional.
                  </p>
                </div>

                <div className="bg-card rounded-lg p-4 border border-warning/30">
                  <p className="text-sm font-medium text-text-primary mb-1">Uncertainty</p>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    Peak height velocity is estimated from patterns in repeated height measurements. Estimates can be wrong, especially when measurements are infrequent or inconsistent. The app will show a confidence grade, and it may refuse to estimate peak height velocity when data quality is insufficient.
                  </p>
                </div>

                <div className="bg-card rounded-lg p-4 border border-warning/30">
                  <p className="text-sm font-medium text-text-primary mb-1">Measurement Error</p>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    Small errors can create false growth spurts in the data. Measure height in the same way each time, ideally at the same time of day, and use two back to back measures.
                  </p>
                </div>

                <div className="bg-card rounded-lg p-4 border border-warning/30">
                  <p className="text-sm font-medium text-text-primary mb-1">Training Advice Boundaries</p>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    The guidance provided is general. It is not a personalised training plan. Children develop at different rates, and decisions about sport and exercise should consider enjoyment, readiness, supervision, and the advice of qualified coaches or clinicians.
                  </p>
                </div>

                <div className="bg-card rounded-lg p-4 border border-warning/30">
                  <p className="text-sm font-medium text-text-primary mb-1">Safeguarding and Data</p>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    This app stores information about a child. Use it only as a parent or guardian, keep login details private, and delete data you no longer need.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-lg p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-3">What You Will Need</h3>
            <ul className="space-y-2 text-text-secondary">
              <li className="flex items-start">
                <Icon name="Check" className="w-5 h-5 text-success mr-2 mt-0.5 flex-shrink-0" />
                <span>A wall and a book for accurate height measurements</span>
              </li>
              <li className="flex items-start">
                <Icon name="Check" className="w-5 h-5 text-success mr-2 mt-0.5 flex-shrink-0" />
                <span>Regular monthly measurements (fortnightly during rapid growth if feasible)</span>
              </li>
              <li className="flex items-start">
                <Icon name="Check" className="w-5 h-5 text-success mr-2 mt-0.5 flex-shrink-0" />
                <span>Consistency in measurement time of day</span>
              </li>
              <li className="flex items-start">
                <Icon name="Check" className="w-5 h-5 text-success mr-2 mt-0.5 flex-shrink-0" />
                <span>Two back-to-back measurements for better accuracy</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex justify-center">
          <Button 
            onClick={onContinue}
            size="lg"
            iconName="ArrowRight"
            iconPosition="right"
          >
            Continue to Consent
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeSection;